/**
 * Single shared date/time formatter for the application layer (FR-027). Every rendered
 * timestamp is pinned explicitly to `Europe/Paris`, never inferred from the viewer's machine
 * (FR-008), and every empty value renders the existing em-dash placeholder rather than a blank
 * cell or `Invalid Date`.
 *
 * Exactly two formats exist:
 * - {@link formatDate} — day only (`25/09/2026`), for due dates and day-valued boundaries
 *   (`follow_up_date`, delegation validity windows, the daily aggregate key).
 * - {@link formatDateTime} — day and hour (`25/09/2026 - 18:30`), for the eight courrier
 *   timestamps that are real event instants (FR-024).
 */

const EMPTY_PLACEHOLDER = '—';

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Renders a day-only instant, e.g. `25/09/2026`. Empty input renders '—'. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_PLACEHOLDER;
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(toDate(value));
}

/** Renders a day-and-hour instant, e.g. `25/09/2026 - 18:30`. Empty input renders '—'. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return EMPTY_PLACEHOLDER;
  const date = toDate(value);
  const hour = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${formatDate(date)} - ${hour}`;
}
